<template>
  <MissionTipsInPage :missionType="6" />
  <div class="bm-container-fluid bm2xl:max-w-[1280px] ibm-font">
    <div :class="$style.container">
      <div :class="$style.left">
        <div>
          <div :class="$style.title">
            {{ $t('cards.home.title') }}
          </div>
          <div :class="$style.subtitle">
            {{ $t('cards.home.subtitle') }}
          </div>
        </div>
        <coin-list :type="CoinType.HOT" :data="staticCoinList?.hotList"></coin-list>
        <coin-list :type="CoinType.NEW" :data="staticCoinList?.newList"></coin-list>
      </div>

      <!-- 交易区 -->
      <div :class="$style.right">
        <div :class="$style.tradeWrapper">
          <div :class="$style.cardTitle">
            {{ $t('cards.buy_crypto') }}
          </div>

          <div :class="$style.form">
            <div :class="$style.inputWrapper">
              <div :class="$style.inputLabel">{{ $t('cards.home.you_will_pay') }}</div>
              <NativeNumInput
                :class="[$style.input, limitDisabled ? $style.inputError : '']"
                :placeholder="$t('earn.enter_amount')"
                v-model="amount"
                :precision="currentCurrency?.scale ?? 2"
                @input="debouncedCalcSizeByAmount"
              />
              <SelectSearch :class="$style.search" :options="currencyList" v-model="currency" is-fait />
            </div>
            <div :class="[$style.inputExtraInfo, limitDisabled ? $style.inputError : '']">
              <span style="margin-right: 2px">{{ $t('cards.home.limit') }}</span>
              <span
                >{{ simpleThousanded(currentCurrency?.minAmount || 0) }} -
                {{ simpleThousanded(currentCurrency?.maxAmount || 0) }}</span
              >
              <span style="margin-left: 2px">{{ currency || '-' }}</span>
            </div>

            <div :class="$style.inputWrapper" style="margin-top: 20px">
              <div :class="$style.inputLabel">{{ $t('cards.home.you_receive') }}</div>
              <NativeNumInput
                :class="[$style.input, $style.inputDisabled]"
                :placeholder="'0.00'"
                :disabled="true"
                v-model="size"
                :precision="currentCrypto?.scale ?? 2"
              />
              <!-- @input="onSizeChange" -->
              <SelectSearch :class="$style.search" :options="cryptoList" v-model="crypto" />
            </div>
            <div :class="$style.inputExtraInfo" style="height: 16px">
              <span v-if="price">
                {{ `1 ${crypto || ''} ≈ ${simpleThousanded(price)} ${currency || ''}` }}
              </span>
            </div>
          </div>
          <client-only>
          <cards
            :channel-type="channelType"
            :currency="currency"
            :card-id="cardId"
            :add-card-form-value="{ amount, crypto, size }"
            @update:card-id="(v) => (cardId = v)"
            @update:cardHolderVisble="updateCardHorderVisible"
          />
          </client-only>
          <div style="height: 64px">
            <BindCardFailTip :order="lastFailCardOrder" />
          </div>
          <!-- form-end -->
          <el-button
            :disabled="!cardId || limitDisabled || !amount"
            :class="$style.submitBtn"
            type="primary"
            size="large"
            @click="onSubmit"
          >
            <span>{{ $t('cards.buy_which', [crypto]) }}</span>
          </el-button>
        </div>
      </div>
    </div>
    <ClientOnly>
      <CardHolderForm
        :type="isCardHorderUpdate?'update':''"
        :visible="cardHolderVisible"
        :currency="currency"
        :channel-type="channelType"
        :add-card-form-value="{ amount, crypto, size }"
        :card-id="cardId"
        @update:visible="updateCardHorderVisible"
      ></CardHolderForm>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import qs from 'qs';
import BigNumber from 'bignumber.js';
import { debounce } from 'lodash-es';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { track, SENSOR_EVENT_NAME } from '~/frontend-shared/helper/sensor';

import coinList from './components/coin-list.vue';
import NativeNumInput from '~/components/global/NativeNumInput.vue';
import SelectSearch from './components/search-pane.vue';
import Cards from './components/cards.vue';
import { CoinType, CardTradeStorageKey, CountryCurrency } from '~/utils/cards/type';
import { priceQuote_post, queryQuote_post, cardOrderDetailFail_get } from '~/api/cards/trade';
import { cryptoShowList_get, currencyList_get, cryptoList_get } from '~/api/cards/backend';
import { useIndexStore } from '~/store/index';
import { useCardsStore } from '~/store/cards';
import { simpleThousanded } from '~/utils/cards/format';
import BindCardFailTip from './components/BindCardFailTip.vue';
import MissionTipsInPage from '~/frontend-shared/components/global/MissionTipsInPage.vue';
import CardHolderForm from './components/card-holder-form.vue';

definePageMeta({
  layout: 'cards',
  name: 'cards',
});

const { t } = useI18n();

const indexStore = useIndexStore();
const localeString = computed(() => {
  return indexStore.normalizedLocaleWithCenterLine;
});
const router = useRouter();
const route = useRoute();

// 接口源数据
const currencyList = ref(),
  cryptoList = ref(),
  staticCoinList = ref();

// 提交交易的表单数据
const currency = ref(),
  amount = ref(),
  crypto = ref(),
  size = ref(),
  cardId = ref(),
  price = ref();

const quoteSide = ref<'fiat' | 'crypto'>('fiat');

const lastFailCardOrder = ref();

const SIDE = 'buy'; // 当前固定交易方向

const cardHolderVisible = ref(false); // 控制持卡人信息表单的显示与隐藏
const isCardHorderUpdate = ref(false); // 控制持卡人信息表单的更新状态
const channelType = 1; // 固定交易类型 mercuryo:1; 未来或许扩展 取currency-list

// Vue 初始化的同步阶段，不会重复执行，目的是：用url参数初始化，避免watch更新
function initByRouterQuery() {
  const searchParams = qs.parse(route.query as unknown as string);
  if (searchParams?.currency) {
    currency.value = searchParams?.currency;
  }
  if (searchParams?.crypto) {
    crypto.value = searchParams?.crypto;
  }
  if (searchParams?.size) {
    size.value = searchParams?.size;
  }
  if (searchParams?.amount) {
    amount.value = searchParams?.amount;
  }
  if (searchParams?.cardId) {
    cardId.value = !isNaN(Number(searchParams?.cardId)) ? Number(searchParams?.cardId) : '';
  }
  if (window.location.search?.includes('from=bind')) {
    const { pathname } = window.location;
    // console.log('reset my path', pathname);
    // 更新地址栏但不刷新页面
    window.history.replaceState({}, '', pathname);
  }
}
if (process.client) {
  initByRouterQuery();
}

// ----- computed -----
const currentCurrency = computed(() => {
  return currencyList.value?.find?.((i: any) => i.currency === currency.value);
});

const currentCrypto = computed(() => {
  return cryptoList.value?.find((i: any) => i.crypto === crypto.value);
});

const limitDisabled = computed(() => {
  if (!amount.value) {
    return false;
  }
  const amountValue = new BigNumber(getRawValue(amount.value) || '');

  return (
    amountValue.isLessThan(currentCurrency.value?.minAmount) ||
    amountValue.isGreaterThan(currentCurrency.value?.maxAmount)
  );
});

const getRawValue = (v?: string) => {
  return v?.replace?.(/,/g, '');
};

 

let sizeUpdating = false;
let amountUpdating = false;

//  ------- define functions -------

/** 根据currency查询可交易的虚拟币对
 *  并且设置默认选中的虚拟币（当前没有选中的crypto或者选中的crypto不在列表中
 */
function getCryptoListByCurrency() {
  if (!currency.value) return;
  cryptoList_get({
    data: {
      channelType,
      currency: currency.value,
      side: SIDE,
    },
  }).then((res) => {
    const list = res?.data?.cryptoList ?? [];
    cryptoList.value =
      list?.map((item, index) => ({
        ...item,
        key: `id_${item.crypto || 'n'}_${index}`,
        label: item.crypto,
        value: item.crypto,
        extraData: item.cryptoFullName,
      })) ?? [];
    if (!crypto.value || !list?.find((i) => i.crypto === crypto.value)) {
      crypto.value = list[0]?.crypto;
    }
  });
}

function getCurrencyList() {
  return currencyList_get({ data: { side: SIDE } }).then((res) => {
    const list = res?.data?.currencyList ?? [];
    currencyList.value =
      list?.map((item, index) => ({
        ...item,
        key: `id_${item.currency || 'n'}_${index}`,
        label: item.currency,
        value: item.currency,
      })) ?? [];
  });
}

const getSizeByAmount = (v?: string) => {
  quoteSide.value = 'fiat';
  sizeUpdating = true;
  const amountValue = v ? getRawValue(v) : '';
  // 添加当前输入值的快照
  const currentInputSnapshot = amount.value;
  const amountNum = new BigNumber(amountValue || '');
  if (
    !amountValue ||
    !amountNum ||
    amountNum.isLessThanOrEqualTo(0) ||
    amountNum.isGreaterThan(currentCurrency.value?.maxAmount) ||
    amountNum.isLessThan(currentCurrency.value?.minAmount)
  ) {
    // size.value = '0';
    size.value = '';
  } else {
    const data = {
      currency: currency.value,
      amount: amountValue,
      crypto: crypto.value,
      channelType,
      side: SIDE,
    };

    // Crypto_Size = Currency_Amount/Price
    queryQuote_post({ data }).then((res) => {
      nextTick(() => {
        // 等响应周期完成后重置标志，避免watch更新
        // 添加检查：如果当前输入值已经改变，则忽略这个响应
        if (currentInputSnapshot !== amount.value) {
          return;
        }
        price.value = res?.data?.price || '';
        size.value = res?.data?.size;
        return res?.data?.size;
      });
    });
    //amountNum.div(currentCrypto.value?.price?? 0).toFixed(currentCrypto.value.scale, BigNumber.ROUND_DOWN);
  }
};

const debouncedCalcSizeByAmount = debounce(getSizeByAmount, 500);

/**
 * 根据法币金额amount计算虚拟币数量size debounce
 */
function calcSizeByAmount_bak(v?: string) {
  quoteSide.value = 'fiat';

  sizeUpdating = true;
  const amountValue = v ? getRawValue(v) : '';
  // 添加当前输入值的快照
  const currentInputSnapshot = amount.value;
  const amountNum = new BigNumber(amountValue || '');
  console.log(`[AmountChange-getSize] (${currency.value}):`, v, `(amountNum: ${amountNum})`, `(${crypto.value})size:`);

  if (
    !amountValue ||
    !amountNum ||
    amountNum.isLessThanOrEqualTo(0) ||
    amountNum.isGreaterThan(currentCurrency.value?.maxAmount) ||
    amountNum.isLessThan(currentCurrency.value?.minAmount)
  ) {
    // size.value = '0';
    size.value = '';
  } else {
    const data = {
      currency: currency.value,
      amount: amountValue,
      crypto: crypto.value,
      channelType,
      side: SIDE,
    };

    // Crypto_Size = Currency_Amount/Price
    queryQuote_post({ data }).then((res) => {
      // 添加检查：如果当前输入值已经改变，则忽略这个响应
      if (currentInputSnapshot !== amount.value) {
        return;
      }
      console.log('query  :', res.data);
      price.value = res?.data?.price || '';

      size.value = res?.data?.size;
      return res?.data?.size;
    });
    //amountNum.div(currentCrypto.value?.price?? 0).toFixed(currentCrypto.value.scale, BigNumber.ROUND_DOWN);
  }
  // size.value = sizeValue;
  // sizeUpdating = false;
  nextTick(() => {
    // 等响应周期完成后重置标志，避免watch更新
    sizeUpdating = false;
  });
}

// 不再存在这种
function calcAmountBySize_bak(v?: string) {
  quoteSide.value = 'crypto';

  amountUpdating = true;

  const sizeValue = v ? getRawValue(v) : '';
  const sizeNum = new BigNumber(sizeValue || '');
  console.log(`[sizeChange-getAmount] crypto(${crypto.value}):`, v, `, (${currency.value})size:`);

  let amountValue;
  if (sizeNum.isLessThanOrEqualTo(0) || !currentCrypto.value?.price) {
    amountValue = '';
    // return 0;
  } else {
    // Currency_Amount = Crypto_Size*Price
    amountValue = sizeNum
      .times(currentCrypto.value?.price ?? 0)
      .toFixed(currentCurrency.value.scale, BigNumber.ROUND_DOWN);
  }
  amount.value = amountValue;
  // amountUpdating = false;
  console.log('     new amount: ', amountValue, ';');
  nextTick(() => {
    amountUpdating = false; // 等响应周期完成后重置标志
  });
}

// ------- action method ------
const onAmountChange = (v?: string) => {
  // if(sizeUpdating || amountUpdating) return;
  const amountValue = v ? getRawValue(v) : '';

  const amountNum = new BigNumber(amountValue || '');
  console.log(`[AmountChange-getSize] (${currency.value}):`, v, `(amountNum: ${amountNum})`, `(${crypto.value})size:`);

  if (
    !v ||
    amountNum.isNaN() ||
    amountNum.isLessThanOrEqualTo(0) ||
    amountNum.isGreaterThan(currentCurrency.value?.maxAmount) ||
    amountNum.isLessThan(currentCurrency.value?.minAmount)
  ) {
    // size.value = '0';
    size.value = '';
  } else {
    debouncedCalcSizeByAmount(v);
  }
};

// const onSizeChange = (v?:string)=>{
//   if(amountUpdating || sizeUpdating) return;
//   calcAmountBySize(v);
// };

function updateCardHorderVisible(visible: boolean) {
  if(!visible){
    isCardHorderUpdate.value = false;
    cardHolderVisible.value = false;
    // 关闭后刷新userInfo
    cardsStore.getUserInfo(true);  // 假设这是一个获取用户信息的方法
  } else {
    cardHolderVisible.value = true; // 设置为true，表示正在更新卡信息
  }
}

const confirmLoading = ref(false);

const onSubmit = async () => {
  confirmLoading.value = true;
  // 国家/KYC 验证
  const needVerified = await cardsStore.openPreValidation();
  if (needVerified) {
    confirmLoading.value = false;
    return;
  }

  // 检查卡信息是否缺少生日信息
  if(cardId.value && !cardsStore.userInfo?.birthday){
    isCardHorderUpdate.value = true;
    cardHolderVisible.value = true;
    ElMessage({
      type: 'error',
      message: t('cards.card-manage.please_input_date')
    });
    return;
  }
  
  const data = {
    currency: currency.value,
    amount: getRawValue(amount.value),
    crypto: crypto.value,
    size: getRawValue(size.value),
    cardId: cardId.value,
    quoteSide: quoteSide.value,
    // card: cardList.value.find((i:any) => i.cardId === cardId.value),
    channelType,
    side: SIDE,
  };
  track(SENSOR_EVENT_NAME.CARDS.click_buy, {
    web_fiat_card_buy_coin: data.crypto,
    web_fiat_card_buy_fiat: data.currency,
    web_fiat_card_buy_coinamount: data.size,
    web_fiat_card_buy_fiatamount: data.amount,
    web_fiat_fiatgateway_buy_kyccountry: cardsStore.userInfo.kycCountry,
  });

  const quoteRes = await priceQuote_post({
    data: {
      ...data,
      quoteType: quoteSide.value,
      sourceAmount: quoteSide.value === 'crypto' ? data.size : data.amount,
    },
  });
  if (!quoteRes?.success) {
    // 提前询价，校验
    confirmLoading.value = false;
    return;
  }
  confirmLoading.value = false;

  // console.log('go to /cards/trade data: ', data);
  sessionStorage.setItem(CardTradeStorageKey, JSON.stringify(data));

  // 最好带上语言 防止重定向，
  router.push({
    path: `/cards/trade/${localeString.value}`,
    //name: 'cards-trade',
    state: data,
  });
};

// ----- watch -----
// currency变化 获取交易对; 防止初始化时，getcrypto不执行
watch(
  currency,
  () => {
    getCryptoListByCurrency();
  },
  { immediate: true }
);

// watch(crypto, () => {
//   // crypto变化 根据currency的amount重新计算size
//   // size.value = '';
// });

// 切换currency 之后，cryoto 不一定变化了，法币去重新查询虚拟币，当汇率变化的时候，重新计算
watch([currentCrypto], (newCryotoItem, old) => {
  console.log('currentCrypto: ', newCryotoItem, old);
  getSizeByAmount(amount.value);

  // @ts-ignore
  // if (newCryotoItem?.price !== currentCrypto.value?.price) {
  //   // 汇率变化 重新计算, 根据上次一次的quoteSide 来判断
  //   // 1. 当quoteSide 为 currency 时，amount变化 根据currency的amount重新计算size
  //   // 2. 当quoteSide 为 crypto 时，size变化 根据size重新计算amount
  //   if (quoteSide.value !== 'crypto') {
  //     calcSizeByAmount(amount.value);
  //   } else {
  //     // 基本不再存在这个可能
  //     calcAmountBySize(size.value);
  //   }
  // }
});

// size变化 根据currency的amount重新计算size
// watch(size, () => {
//   if(sizeUpdating || amountUpdating) return;
//   calcAmountBySize(size.value);
// });

// amount变化 根据currency的amount重新计算size
// watch(amount, () => {
//   if (amountUpdating || sizeUpdating) return;
//   calcSizeByAmount(amount.value);
// });

const cardsStore = useCardsStore();
onMounted(() => {
  // initByRouterQuery();

  // 左侧展示列表 hot和new
  cryptoShowList_get().then((res) => {
    staticCoinList.value = res?.data ?? [];
  });

  Promise.all([getCurrencyList(), cardsStore.getUserInfo()]).then(() => {
    // 根据userInfo的国家获取法币
    const defaultCurrency = currency.value || CountryCurrency[cardsStore.userInfo?.kycCountry || ''];

    // 如果当前默认法币是空 或者不在列表中，则取第一个
    if (!defaultCurrency || !currencyList.value?.find((i: any) => i.currency === defaultCurrency)) {
      currency.value = currencyList.value[0]?.currency;
    } else if (defaultCurrency !== currency.value) {
      currency.value = defaultCurrency;
    }
  });

  cardOrderDetailFail_get().then((res) => {
    if (res?.data?.orderNo) {
      lastFailCardOrder.value = res.data;
    }
  });
});
</script>

<style lang="css" module>
.container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-top: 60px;
  gap: 20px;
}
.left {
  width: 560px;
  max-width: 560px;
  min-width: 300px;
  flex: 1;
}
.right {
  width: 406px;
  max-width: 406px;
  min-width: 300px;
  flex-shrink: 1;
  flex-grow: 0;
  margin-bottom: 40px;
}
.title {
  font-weight: 700;
  font-size: 48px;
  margin-right: 160px;
  @media screen and (max-width: 768px) {
    margin-right: 12px;
    font-size: 32px;
  }
}
.subtitle {
  margin-top: 12px;
  margin-bottom: 76px;
  font-weight: 400;
  font-size: 16px;
}
.tradeWrapper {
  border: 2px solid #e9f0f3;
  border-radius: 12px;
  padding: 24px 23px 40px;

  .cardTitle {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .form {
    position: relative;
    z-index: 1;
  }
  .inputWrapper {
    display: inline-block;
    position: relative;
    width: 100%;
    .inputLabel {
      position: absolute;
      margin-top: 18px;
      margin-left: 20px;
      font-size: 12px;
      color: var(--color-p2p-text-primary-400);
    }

    .search {
      position: absolute;
      bottom: 12px;
      right: 20px;
      margin-bottom: 6px;
    }
  }

  .input {
    padding: 40px 120px 18px 20px;
    width: 100%;
    box-sizing: border-box;
    font-size: 24px;
    font-weight: 520;
    background-color: var(--color-p2p-bg-line);
    border-radius: 8px;
    border: 1px solid var(--color-p2p-bg-line);
    transition: border 0.3s ease;
    &:focus {
      border: 1px solid var(--color-p2p-text-effect);
    }
    &.inputError {
      border: 1px solid var(--color-error);
    }
    &::placeholder {
      font-weight: 500;
      color: var(--color-input-placeholder-text);
    }
  }
  .input.inputDisabled {
    cursor: not-allowed;
    background-color: rgba(239, 244, 246, 0.4);
    border-color: rgba(239, 244, 246, 0.4);
  }
  .inputExtraInfo {
    margin-top: 12px;
    font-size: 12px;
    color: var(--color-input-placeholder-text);
    line-height: 15.6px;
    transition: color 0.3s ease;
    &.inputError {
      color: var(--color-error);
    }
  }
}
.submitBtn {
  width: 100%;
  margin: 12px 0 0;
}
</style>
